import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Copy, ExternalLink, LogOut, DollarSign, MousePointerClick, ShoppingCart, Wallet, BarChart3 } from 'lucide-react';
import FounderAffiliateAnalytics from '../components/FounderAffiliateAnalytics';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InfluencerDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingPayout, setSavingPayout] = useState(false);
  const [payout, setPayout] = useState(null);
  const [tab, setTab] = useState('analytics');

  useEffect(() => {
    (async () => {
      try {
        const [m, s, c] = await Promise.all([
          axios.get(`${API}/api/influencer/me`, { withCredentials: true }),
          axios.get(`${API}/api/influencer/stats`, { withCredentials: true }),
          axios.get(`${API}/api/influencer/commissions`, { withCredentials: true }),
        ]);
        setMe(m.data);
        setStats(s.data);
        setCommissions(c.data.items || []);
        setPayout({
          payout_method: m.data.payout_method || 'unset',
          paypal_email: m.data.paypal_email || '',
          stripe_connect_account_id: m.data.stripe_connect_account_id || '',
        });
      } catch (err) {
        navigate('/influencer/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const savePayout = async () => {
    setSavingPayout(true);
    try {
      await axios.post(`${API}/api/influencer/payout-info`, payout, { withCredentials: true });
      toast.success('Payout info saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingPayout(false);
    }
  };

  const copyLink = () => {
    if (me?.tracking_url) {
      navigator.clipboard.writeText(me.tracking_url);
      toast.success('Link copied');
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
    </div>;
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="influencer-dashboard">
      <header className="border-b border-white/10 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-[#00F0FF] font-bold tracking-tight">theailedger.ai</Link>
          <div className="text-sm text-neutral-300">
            Hi, {me?.name} <span className="text-neutral-500">· {me?.handle || me?.email}</span>
          </div>
        </div>
      </header>

      {/* Tracking link card */}
      <section className="max-w-5xl mx-auto px-6 pt-8">
        {payout?.payout_method === 'unset' && (
          <div className="mb-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/40 flex items-start gap-3" data-testid="payout-setup-banner">
            <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-amber-300 font-bold">!</span>
            </div>
            <div>
              <div className="font-semibold text-amber-100">Set up your payout method below</div>
              <div className="text-sm text-amber-200/70 mt-0.5">You can earn commissions right away, but we need to know where to send the money. Scroll to the bottom and pick PayPal or Stripe Connect.</div>
            </div>
          </div>
        )}
        <div className="bg-gradient-to-br from-[#00F0FF]/10 to-transparent border border-[#00F0FF]/30 rounded-2xl p-5 sm:p-6">
          <div className="text-xs uppercase tracking-wide text-[#00F0FF] font-bold mb-2">Your tracking link</div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <code className="text-lg font-mono text-white break-all flex-1" data-testid="dashboard-tracking-url">
              {me?.tracking_url}
            </code>
            <button onClick={copyLink} data-testid="dashboard-copy-link"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-black font-semibold rounded-lg hover:bg-[#00F0FF]/90 text-sm">
              <Copy className="w-4 h-4" /> Copy
            </button>
          </div>
          <p className="text-sm text-neutral-400 mt-3">
            Paste this in your IG/TikTok/X bio. Every viewer who clicks gets a 90-day cookie —
            you earn <span className="text-[#00F0FF] font-semibold">{me?.commission_rate}%</span>{' '}
            on anything they buy on theailedger.ai during that window.
          </p>
        </div>
      </section>

      {/* Tab switcher */}
      <section className="max-w-5xl mx-auto px-6 pt-6">
        <div className="flex gap-1 border-b border-white/10" data-testid="influencer-tabs">
          {[
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'overview', label: 'Overview', icon: Wallet },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
                    data-testid={`tab-${t.id}`}
                    className={`px-4 py-2 text-sm font-semibold border-b-2 transition flex items-center gap-2 -mb-px ${
                      tab === t.id
                        ? 'border-[#00F0FF] text-[#00F0FF]'
                        : 'border-transparent text-neutral-400 hover:text-white'
                    }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </section>

      {tab === 'analytics' && (
        <section className="max-w-5xl mx-auto px-6 pt-6 pb-12" data-testid="analytics-tab">
          <FounderAffiliateAnalytics
            endpoint={`${API}/api/influencer/analytics`}
            withCredentials={true}
          />
        </section>
      )}

      {tab === 'overview' && (
        <>
      {/* Stats grid */}
      <section className="max-w-5xl mx-auto px-6 pt-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat icon={MousePointerClick} label="Clicks" value={stats?.total_clicks ?? 0} accent="#00F0FF" />
          <Stat icon={ShoppingCart} label="Conversions" value={stats?.total_conversions ?? 0} accent="#a78bfa" />
          <Stat icon={Wallet} label="Owed (USD)" value={`$${(stats?.total_owed ?? 0).toFixed(2)}`} accent="#f59e0b" />
          <Stat icon={DollarSign} label="Paid (USD)" value={`$${(stats?.total_paid ?? 0).toFixed(2)}`} accent="#34d399" />
        </div>
      </section>

      {/* Commissions table */}
      <section className="max-w-5xl mx-auto px-6 pt-8 pb-8">
        <h2 className="text-base font-bold uppercase tracking-wide text-[#00F0FF] mb-3">Commissions</h2>
        {commissions.length === 0 ? (
          <p className="text-sm text-neutral-500 bg-neutral-900/40 border border-white/10 rounded-xl p-6 text-center">
            No commissions yet. Once viewers click your link and buy on the marketplace, they&apos;ll show up here.
          </p>
        ) : (
          <div className="overflow-x-auto border border-white/10 rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-neutral-400 uppercase text-xs">
                <tr>
                  <th className="text-left p-3 font-semibold">Date</th>
                  <th className="text-left p-3 font-semibold">Source</th>
                  <th className="text-right p-3 font-semibold">Gross</th>
                  <th className="text-right p-3 font-semibold">Commission</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((c) => (
                  <tr key={c.commission_id} className="border-t border-white/10" data-testid="commission-row">
                    <td className="p-3 text-neutral-400">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="p-3 text-neutral-300">{c.source.replace(/_/g, ' ')}</td>
                    <td className="p-3 text-right text-neutral-300">${c.gross_amount.toFixed(2)}</td>
                    <td className="p-3 text-right text-white font-semibold">${c.commission_amount.toFixed(2)}</td>
                    <td className="p-3">
                      <StatusPill status={c.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Payout info */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <h2 className="text-base font-bold uppercase tracking-wide text-[#00F0FF] mb-3">Payout method</h2>
        <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-5 space-y-4">
          <div className="flex gap-2">
            {['paypal', 'stripe_connect'].map((m) => (
              <button key={m} onClick={() => setPayout((p) => ({ ...p, payout_method: m }))}
                      data-testid={`payout-${m}`}
                      className={`px-4 py-2 rounded-lg text-sm transition ${payout?.payout_method === m ? 'bg-[#00F0FF] text-black font-semibold' : 'bg-white/5 text-neutral-300 hover:bg-white/10'}`}>
                {m === 'paypal' ? 'PayPal' : 'Stripe Connect'}
              </button>
            ))}
          </div>

          {payout?.payout_method === 'paypal' && (
            <input type="email" value={payout.paypal_email}
                   onChange={(e) => setPayout((p) => ({ ...p, paypal_email: e.target.value }))}
                   data-testid="payout-paypal-email"
                   placeholder="PayPal email"
                   className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]" />
          )}
          {payout?.payout_method === 'stripe_connect' && (
            <input value={payout.stripe_connect_account_id}
                   onChange={(e) => setPayout((p) => ({ ...p, stripe_connect_account_id: e.target.value }))}
                   placeholder="acct_..."
                   className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]" />
          )}

          <button onClick={savePayout} disabled={savingPayout}
                  data-testid="payout-save"
                  className="inline-flex items-center gap-2 px-5 py-2 bg-[#00F0FF] text-black font-semibold rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50">
            {savingPayout && <Loader2 className="w-4 h-4 animate-spin" />}
            Save payout info
          </button>
        </div>
      </section>
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4">
      <Icon className="w-4 h-4 mb-2" style={{ color: accent }} />
      <div className="text-xs uppercase text-neutral-500 tracking-wide">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: 'bg-amber-500/10 text-amber-400',
    approved: 'bg-blue-500/10 text-blue-400',
    paid: 'bg-emerald-500/10 text-emerald-400',
    reversed: 'bg-red-500/10 text-red-400',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full uppercase tracking-wide ${map[status] || 'bg-white/5 text-neutral-400'}`}>{status}</span>;
}
